import { useCallback, useEffect, useState } from "react";
import {
  createAccountGroup,
  deleteAccountGroup,
  listAccountGroups,
  lookupGroupCandidates,
  updateAccountGroup,
} from "services/accountGroups";
import { AccountGroup, CandidateFamily } from "types/accountGroup";

function errorMessage(error: any): string {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong. Please try again."
  );
}

function ungroupedIds(family: CandidateFamily): string[] {
  return family.profiles
    .filter((profile) => !profile.group_id)
    .map((profile) => profile.user_id);
}

function existingMemberIds(family: CandidateFamily): string[] {
  return family.profiles
    .filter((profile) => profile.group_id === family.existing_group_id)
    .map((profile) => profile.user_id);
}

export function useAccountGroups() {
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [families, setFamilies] = useState<CandidateFamily[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [lookingUp, setLookingUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLookedUp, setHasLookedUp] = useState(false);

  const refreshGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      setGroups(await listAccountGroups());
      setError(null);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => {
    refreshGroups();
  }, [refreshGroups]);

  const lookup = async (email?: string) => {
    setLookingUp(true);
    setHasLookedUp(true);
    try {
      setFamilies(await lookupGroupCandidates(email));
      setError(null);
    } catch (requestError) {
      setFamilies([]);
      setError(errorMessage(requestError));
    } finally {
      setLookingUp(false);
    }
  };

  const mutate = async (request: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await request();
      setError(null);
      await refreshGroups();
      return true;
    } catch (requestError) {
      setError(errorMessage(requestError));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const createGroup = async (userIds: string[]) => {
    const created = await mutate(() => createAccountGroup(userIds));
    if (created) {
      setFamilies((current) =>
        current.filter(
          (family) =>
            !family.profiles.some((profile) =>
              userIds.includes(profile.user_id),
            ),
        ),
      );
    }
    return created;
  };

  const mergeIntoGroup = async (family: CandidateFamily, userIds: string[]) => {
    if (!family.existing_group_id) {
      return createGroup(userIds);
    }
    const mergedIds = Array.from(
      new Set([...existingMemberIds(family), ...userIds]),
    );
    const merged = await mutate(() =>
      updateAccountGroup(family.existing_group_id as string, mergedIds),
    );
    if (merged) {
      setFamilies((current) =>
        current.filter((entry) => entry.id !== family.id),
      );
    }
    return merged;
  };

  const createAllGroups = async () => {
    const actionableFamilies = families.filter((family) => {
      const available = ungroupedIds(family);
      return (
        available.length > 1 ||
        (available.length > 0 && Boolean(family.existing_group_id))
      );
    });
    if (actionableFamilies.length === 0) return false;

    setSaving(true);
    const results = await Promise.all(
      actionableFamilies.map(async (family) => {
        try {
          const available = ungroupedIds(family);
          if (family.existing_group_id) {
            await updateAccountGroup(
              family.existing_group_id,
              Array.from(new Set([...existingMemberIds(family), ...available])),
            );
          } else {
            await createAccountGroup(available);
          }
          return { id: family.id, error: null };
        } catch (requestError) {
          return { id: family.id, error: requestError };
        }
      }),
    );

    const createdIds = new Set(
      results.filter((result) => !result.error).map((result) => result.id),
    );
    const failedResult = results.find((result) => result.error);

    setFamilies((current) =>
      current.filter((family) => !createdIds.has(family.id)),
    );
    await refreshGroups();
    if (failedResult) {
      setError(errorMessage(failedResult.error));
    }
    setSaving(false);

    return !failedResult;
  };

  return {
    groups,
    families,
    loadingGroups,
    lookingUp,
    saving,
    error,
    hasLookedUp,
    lookup,
    createGroup,
    mergeIntoGroup,
    createAllGroups,
    updateGroup: (id: string, userIds: string[]) =>
      mutate(() => updateAccountGroup(id, userIds)),
    deleteGroup: (id: string) => mutate(() => deleteAccountGroup(id)),
  };
}
